import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { View } from "@dhx/ts-common/view";
import { Layout, ILayoutConfig, ICellConfig } from "@dhx/ts-layout";
import { extend, uid, isDefined, isEmptyObj } from "@dhx/ts-common/core";
import { IAnyObj } from "@dhx/ts-common/types";
import { DatePicker } from "./elements/dateinput";
import { Button } from "./elements/button";
import { Checkbox } from "./elements/checkbox";
import { CheckboxGroup } from "./elements/checkboxGroup";
import { Input } from "./elements/input";
import { RadioGroup } from "./elements/radioGroup";
import { Select } from "./elements/select";
import { Textarea } from "./elements/textarea";
import { Text } from "./elements/textinput";
import { Combo } from "./elements/combo";
import { SliderForm } from "./elements/sliderform";
import { isBlock, isVerify } from "./helper";
import { SimpleVault } from "./elements/simplevault";
import { TimePicker } from "./elements/timeinput";
import { ColorPicker } from "./elements/colorpicker";
import { Spacer } from "./elements/spacer";
import {
	FormEvents,
	IItemConfig,
	IFormEventHandlersMap,
	IForm,
	ClearMethod,
	ItemEvent,
	IFormConfig,
	FormDataCallback,
	IOption,
	ISimpleVaultValue,
	ICheckboxGroupValue,
	IFormProps,
	IDatePickerConfig,
	ICheckboxGroupConfig,
	IComboConfig,
	IRadioGroupConfig,
	ISelectConfig,
	ISimpleVaultConfig,
	ISliderFormConfig,
	ITimePickerConfig,
	IColorPickerConfig,
} from "./types";
import { ITimeObject } from "@dhx/ts-timepicker";
import { awaitRedraw } from "@dhx/ts-common/dom";

export class Form extends View implements IForm {
	public config: IFormConfig;
	public events: IEventSystem<FormEvents, IFormEventHandlersMap>;
	public layout: Layout;

	private _isValid = true;
	protected _attachments: { [name: string]: any };
	protected _state: { [key: string]: any } = {};
	private container: HTMLElement | string;

	constructor(container: HTMLElement | string, config: IFormConfig) {
		super(
			null,
			extend(
				{
					disabled: false,
					hidden: false,
				},
				config
			)
		);
		this.events = new EventSystem<FormEvents>(this);
		this.container = container;
		this._initUI(container);

		this.config.hidden && this.hide(true);
		this.config.disabled && this.disable();

		this.events.on(FormEvents.afterShow, () => {
			this._formContainerShow();
		});
		this.events.on(FormEvents.afterHide, () => {
			if (!Object.values(this._attachments).some(item => !item.config.hidden)) {
				this._formContainerHide();
			}
		});
		this.events.on(FormEvents.change, () => this.paint());

		awaitRedraw().then(() => {
			const node = this.layout.getRootNode();
			if (node) {
				node.setAttribute("role", "form");
				this.config.title && node.setAttribute("aria-label", this.config.title);
			}
		});
	}
	paint() {
		super.paint();
		this.layout.paint();
	}
	send(url: string, method = "POST", asFormData?: boolean): Promise<any> | void {
		if (this.events.fire(FormEvents.beforeSend)) {
			return new Promise((resolve, reject) => {
				const xhr = new XMLHttpRequest();
				xhr.onload = () => {
					if (xhr.status >= 200 && xhr.status < 300) {
						resolve(xhr.response || xhr.responseText);
					} else {
						reject({
							status: xhr.status,
							statusText: xhr.statusText,
						});
					}
				};
				xhr.onloadend = () => {
					if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
						this.events.fire(FormEvents.afterSend);
					}
				};
				xhr.onerror = () => {
					reject({
						status: xhr.status,
						statusText: xhr.statusText,
					});
				};
				if (method === "GET") {
					url += `?` + encodeURIComponent(JSON.stringify(this.getValue()));
				}
				xhr.open(method, url);
				if (!asFormData) {
					xhr.setRequestHeader("Content-Type", "application/json");
				}
				switch (method) {
					case "POST":
						this._send();
						xhr.send(
							asFormData ? (this.getValue(true) as FormData) : JSON.stringify(this.getValue())
						);
						break;
					case "DELETE":
					case "PUT":
						xhr.send(
							asFormData ? (this.getValue(true) as FormData) : JSON.stringify(this.getValue())
						);
						break;
					case "GET":
						xhr.send();
						break;
					default:
						xhr.send();
						break;
				}
			});
		}
	}
	clear(method?: ClearMethod): void {
		switch (method) {
			case ClearMethod.value:
				this._clear();
				break;
			case ClearMethod.validation:
				this._clearValidate();
				break;
			default:
				this._clear();
				this._clearValidate();
				break;
		}
		this.paint();
	}
	setValue(obj: IAnyObj): void {
		for (const item in obj) {
			for (const key in this._attachments) {
				if (
					typeof this._attachments[key].setValue === "function" &&
					this._attachments[key].config.name === item
				) {
					this._attachments[key].setValue(obj[item]);
				}
			}
		}
	}
	getValue<T extends boolean = false>(asFormData?: boolean): T extends true ? FormData : IAnyObj {
		if (asFormData) {
			const formData = new FormData();
			for (const key in this._state) {
				if (Array.isArray(this._state[key])) {
					this._state[key].forEach((value, i) => formData.append(key + `[${i}]`, value));
				} else {
					formData.append(key, this._state[key]);
				}
			}
			return formData as any;
		}
		return { ...this._state } as any;
	}
	getItem(name: string): any {
		for (const item in this._attachments) {
			if (item == name) {
				return this._attachments[item];
			}
		}
	}
	validate(silent = false): boolean {
		this._isValid = true;
		for (const key in this._attachments) {
			if (typeof this._attachments[key].validate === "function") {
				if (isVerify(this._attachments[key].config) && !this._attachments[key].validate(silent)) {
					this._isValid = false;
					!silent && this.events.fire(FormEvents.validationFail, [key, this._attachments[key]]);
				}
			}
		}
		return this._isValid;
	}
	setProperties(arg: string | { [name: string]: IFormProps }, props?: IFormProps): void {
		if (typeof arg === "string" && props && !isEmptyObj(props)) {
			for (const key in this._attachments) {
				if (typeof this._attachments[key].setProperties === "function" && key === arg) {
					this._attachments[key].setProperties(props);
				}
			}
		}
		if (typeof arg === "object" && !isEmptyObj(arg)) {
			for (const key in this._attachments) {
				if (typeof this._attachments[key].setProperties === "function" && !isEmptyObj(arg[key])) {
					this._attachments[key].setProperties(arg[key]);
				}
			}
		}
	}
	getProperties(name?: string): { [name: string]: IFormProps } | IFormProps {
		if (name) {
			for (const key in this._attachments) {
				if (typeof this._attachments[key].getProperties === "function" && key === name) {
					return this._attachments[key].getProperties();
				}
			}
		}
		const obj: { [name: string]: IFormProps } = {};
		for (const key in this._attachments) {
			if (typeof this._attachments[key].getProperties === "function") {
				obj[key] = this._attachments[key].getProperties();
			}
		}
		return obj;
	}
	show(): void {
		if (!this.config.hidden && !Object.values(this._attachments).some(item => !item.config.hidden)) {
			return;
		}
		this._formContainerShow();
		for (const key in this._attachments) {
			if (typeof this._attachments[key].show === "function") {
				this._attachments[key].show();
			}
		}
	}
	hide(init?: boolean): void {
		if (this.config.hidden && !init) {
			return;
		}
		this._formContainerHide();
		for (const key in this._attachments) {
			if (typeof this._attachments[key].hide === "function") {
				this._attachments[key].hide(init);
			}
		}
	}
	setFocus(name: string): void {
		for (const item in this._attachments) {
			const control = this._attachments[item];
			if (control.config.type === "radiogroup" || control.config.type === "checkboxgroup") {
				control.focus(name);
			}
			if (control.config.name === name) {
				control.focus();
			}
		}
	}
	blur(name: string): void {
		if (!name) {
			this.forEach((control: any) => {
				if (typeof control.blur === "function") {
					control.blur();
				}
			});
		}
		for (const item in this._attachments) {
			const control = this._attachments[item];
			if (control.config.type === "radiogroup" || control.config.type === "checkboxgroup") {
				control.blur(name);
			}
			if (control.config.name === name) {
				control.blur();
			}
		}
	}
	isVisible(name?: string): boolean {
		if (!name) {
			return !this.config.hidden;
		}
		for (const key in this._attachments) {
			if (key === name) {
				return !this._attachments[key].config.hidden;
			}
		}
	}
	disable(): void {
		this.config.disabled = true;
		for (const key in this._attachments) {
			if (typeof this._attachments[key].disable === "function") {
				this._attachments[key].disable();
			}
		}
	}
	enable(): void {
		this.config.disabled = false;
		for (const key in this._attachments) {
			if (typeof this._attachments[key].enable === "function") {
				this._attachments[key].enable();
			}
		}
	}
	isDisabled(name?: string): boolean {
		if (!name) {
			return this.config.disabled;
		}
		for (const key in this._attachments) {
			if (key === name) {
				return this._attachments[key].config.disabled;
			}
		}
	}
	forEach(callback: FormDataCallback): void {
		const items = Object.values(this._attachments);
		for (let index = 0; index < items.length; index++) {
			callback.call(this, items[index], index, items);
		}
	}
	destructor(): void {
		this.events && this.events.clear();
		this.layout && this.layout.destructor();
		this.config = this._attachments = this._state = this._uid = this.container = this.events = this._isValid = null;

		this.unmount();
	}
	getRootView(): any {
		return this.layout.getRootView();
	}
	protected _addLayoutItem(item: IItemConfig) {
		const id = (item.id = item.id || uid());
		const name = (item.name = item.name || id.toString());
		item.type = item.type && (item.type.toLowerCase() as any);
		let { width, height } = item;
		const { css, padding, ...config } = item;
		let classList = css ? css + " dhx_form-element" : "dhx_form-element";
		const autoSize = !(item.type === "spacer" || item.type === undefined);
		if (autoSize && !width) {
			width = "content";
		}
		if (autoSize && !height) {
			height = "content";
		}

		switch (config.type) {
			case "button":
				{
					if (config.full) {
						classList += " dhx_button--full-gravity";
					}
					const button = (this._attachments[name] = new Button(null, item));
					button.events.on(ItemEvent.beforeChangeProperties, props => {
						return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
					});
					button.events.on(ItemEvent.afterChangeProperties, props => {
						this._changeProps(name, props);
						this.events.fire(FormEvents.afterChangeProperties, [name, props]);
						this.paint();
					});
					button.events.on(ItemEvent.click, e => {
						e.preventDefault();
						this.events.fire(FormEvents.click, [name, e]);
						this.events.fire(FormEvents.buttonClick, [name, e]);

						if (button.config.submit && this.validate()) {
							button.config.url && this.send(button.config.url);
						}
					});
					button.events.on(ItemEvent.beforeHide, (text: string, init: boolean) => {
						if (!init) {
							return this.events.fire(FormEvents.beforeHide, [name, text]);
						}
					});
					button.events.on(ItemEvent.beforeShow, (text: string) => {
						return this.events.fire(FormEvents.beforeShow, [name, text]);
					});
					button.events.on(ItemEvent.afterHide, (text: string, init: boolean) => {
						this.layout.getCell(name).hide();
						!init && this.events.fire(FormEvents.afterHide, [name, text]);
					});
					button.events.on(ItemEvent.afterShow, (text: string) => {
						this.layout.getCell(name).show();
						this.events.fire(FormEvents.afterShow, [name, text]);
					});
					button.events.on(ItemEvent.focus, (text: string) => {
						this.events.fire(FormEvents.focus, [name, text]);
					});
					button.events.on(ItemEvent.blur, (text: string) => {
						this.events.fire(FormEvents.blur, [name, text]);
					});
					button.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
						this.events.fire(FormEvents.keydown, [event, name]);
					});
				}
				break;
			case "datepicker": {
				const datePicker = (this._attachments[name] = new DatePicker(
					null,
					item as IDatePickerConfig
				));
				this._state[name] = datePicker.getValue(config.valueFormat === "Date");

				datePicker.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				datePicker.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = datePicker.getValue(config.valueFormat === "Date");
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.paint();
				});
				datePicker.events.on(ItemEvent.change, (value: string | Date) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				datePicker.events.on(ItemEvent.beforeHide, (value: string | Date, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				datePicker.events.on(ItemEvent.beforeShow, (value: string | Date) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				datePicker.events.on(ItemEvent.afterHide, (value: string | Date, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				datePicker.events.on(ItemEvent.afterShow, (value: string | Date) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				datePicker.events.on(ItemEvent.beforeValidate, (value: string | Date) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				datePicker.events.on(ItemEvent.afterValidate, (value: string | Date, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				datePicker.events.on(ItemEvent.focus, (value: string | Date) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				datePicker.events.on(ItemEvent.blur, (value: string | Date) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				datePicker.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "checkbox": {
				const checkbox = (this._attachments[name] = new Checkbox(null, item));
				this._state[name] = checkbox.getValue();

				checkbox.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				checkbox.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = checkbox.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.paint();
				});
				checkbox.events.on(ItemEvent.change, (value: string | boolean) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				checkbox.events.on(ItemEvent.beforeHide, (value: string | boolean, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				checkbox.events.on(ItemEvent.beforeShow, (value: string | boolean) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				checkbox.events.on(ItemEvent.afterHide, (value: string | boolean, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				checkbox.events.on(ItemEvent.afterShow, (value: string | boolean) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				checkbox.events.on(ItemEvent.beforeValidate, (value: string | boolean) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				checkbox.events.on(ItemEvent.afterValidate, (value: string | boolean, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				checkbox.events.on(ItemEvent.focus, (value: string | boolean) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				checkbox.events.on(ItemEvent.blur, (value: string | boolean) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				checkbox.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "checkboxgroup": {
				const checkboxGroup = (this._attachments[name] = new CheckboxGroup(
					null,
					item as ICheckboxGroupConfig
				));
				this._state[name] = checkboxGroup.getValue();

				checkboxGroup.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				checkboxGroup.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = checkboxGroup.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.paint();
				});
				checkboxGroup.events.on(ItemEvent.change, (value: ICheckboxGroupValue) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				checkboxGroup.events.on(ItemEvent.beforeHide, (value: ICheckboxGroupValue, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				checkboxGroup.events.on(ItemEvent.beforeShow, (value: ICheckboxGroupValue) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				checkboxGroup.events.on(ItemEvent.afterHide, (value: ICheckboxGroupValue, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				checkboxGroup.events.on(ItemEvent.afterShow, (value: ICheckboxGroupValue) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				checkboxGroup.events.on(ItemEvent.beforeValidate, (value: ICheckboxGroupValue) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				checkboxGroup.events.on(
					ItemEvent.afterValidate,
					(value: ICheckboxGroupValue, isValid: boolean) => {
						this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
					}
				);
				checkboxGroup.events.on(ItemEvent.focus, (value: ICheckboxGroupValue, id: string) => {
					this.events.fire(FormEvents.focus, [name, value, id]);
				});
				checkboxGroup.events.on(ItemEvent.blur, (value: ICheckboxGroupValue, id: string) => {
					this.events.fire(FormEvents.blur, [name, value, id]);
				});
				checkboxGroup.events.on(ItemEvent.keydown, (event: KeyboardEvent, id) => {
					this.events.fire(FormEvents.keydown, [event, name, id]);
				});
				break;
			}
			case "combo": {
				const combo = (this._attachments[name] = new Combo(null, item as IComboConfig));
				this._state[name] = combo.getValue();

				combo.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				combo.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = combo.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.paint();
				});
				combo.events.on(ItemEvent.change, (value: string | string[]) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				combo.events.on(ItemEvent.beforeHide, (value: string | string[], init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				combo.events.on(ItemEvent.beforeShow, (value: string | string[]) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				combo.events.on(ItemEvent.afterHide, (value: string | string[], init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				combo.events.on(ItemEvent.afterShow, (value: string | string[]) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				combo.events.on(ItemEvent.beforeValidate, (value: string | string[]) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				combo.events.on(ItemEvent.afterValidate, (value: string | string[], isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				combo.events.on(ItemEvent.focus, (value: string | string[]) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				combo.events.on(ItemEvent.blur, (value: string | string[]) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				combo.events.on(ItemEvent.keydown, (event: KeyboardEvent, id: string | undefined) => {
					this.events.fire(FormEvents.keydown, [event, name, id]);
				});
				break;
			}
			case "input": {
				const input = (this._attachments[name] = new Input(null, item));
				this._state[name] = input.getValue();

				input.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				input.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = input.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.paint();
				});
				input.events.on(ItemEvent.change, (value: string | number) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				input.events.on(ItemEvent.beforeHide, (value: string | number, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				input.events.on(ItemEvent.beforeShow, (value: string | number) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				input.events.on(ItemEvent.afterHide, (value: string | number, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				input.events.on(ItemEvent.afterShow, (value: string | number) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				input.events.on(ItemEvent.beforeValidate, (value: string | number) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				input.events.on(ItemEvent.afterValidate, (value: string | number, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				input.events.on(ItemEvent.focus, (value: string | number) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				input.events.on(ItemEvent.blur, (value: string | number) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				input.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "radiogroup": {
				const radioGroup = (this._attachments[name] = new RadioGroup(
					null,
					item as IRadioGroupConfig
				));
				this._state[name] = radioGroup.getValue();

				radioGroup.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				radioGroup.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = radioGroup.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.paint();
				});
				radioGroup.events.on(ItemEvent.change, (value: string) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				radioGroup.events.on(ItemEvent.beforeHide, (value: string, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				radioGroup.events.on(ItemEvent.beforeShow, (value: string) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				radioGroup.events.on(ItemEvent.afterHide, (value: string, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				radioGroup.events.on(ItemEvent.afterShow, (value: string) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				radioGroup.events.on(ItemEvent.beforeValidate, (value: string) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				radioGroup.events.on(ItemEvent.afterValidate, (value: string, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				radioGroup.events.on(ItemEvent.focus, (value: string, id: string) => {
					this.events.fire(FormEvents.focus, [name, value, id]);
				});
				radioGroup.events.on(ItemEvent.blur, (value: string, id: string) => {
					this.events.fire(FormEvents.blur, [name, value, id]);
				});
				radioGroup.events.on(ItemEvent.keydown, (event: KeyboardEvent, id: string) => {
					this.events.fire(FormEvents.keydown, [event, name, id]);
				});
				break;
			}
			case "select": {
				const select = (this._attachments[name] = new Select(null, item as ISelectConfig));
				this._state[name] = select.getValue();

				select.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				select.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = select.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.paint();
				});
				select.events.on(ItemEvent.changeOptions, (options: IOption[]) => {
					this.layout.getCell(name).config.options = [...options];
				});
				select.events.on(ItemEvent.change, (value: string | number) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				select.events.on(ItemEvent.beforeHide, (value: string | number, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				select.events.on(ItemEvent.beforeShow, (value: string | number) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				select.events.on(ItemEvent.afterHide, (value: string | number, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				select.events.on(ItemEvent.afterShow, (value: string | number) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				select.events.on(ItemEvent.beforeValidate, (value: string | number) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				select.events.on(ItemEvent.afterValidate, (value: string | number, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				select.events.on(ItemEvent.focus, (value: string | number) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				select.events.on(ItemEvent.blur, (value: string | number) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				select.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "simplevault": {
				config.$vaultHeight = height;
				const simpleVault = (this._attachments[name] = new SimpleVault(
					null,
					item as ISimpleVaultConfig
				));
				this._state[name] = simpleVault.getValue();

				simpleVault.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				simpleVault.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = simpleVault.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.paint();
				});
				simpleVault.events.on(ItemEvent.change, (value: ISimpleVaultValue[]) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				simpleVault.events.on(ItemEvent.beforeHide, (value: ISimpleVaultValue[], init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				simpleVault.events.on(ItemEvent.beforeShow, (value: ISimpleVaultValue[]) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				simpleVault.events.on(ItemEvent.afterHide, (value: ISimpleVaultValue[], init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				simpleVault.events.on(ItemEvent.afterShow, (value: ISimpleVaultValue[]) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				simpleVault.events.on(ItemEvent.beforeValidate, (value: ISimpleVaultValue[]) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				simpleVault.events.on(
					ItemEvent.afterValidate,
					(value: ISimpleVaultValue[], isValid: boolean) => {
						this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
					}
				);
				break;
			}
			case "slider": {
				const sliderForm = (this._attachments[name] = new SliderForm(
					null,
					item as ISliderFormConfig
				));
				this._state[name] = sliderForm.getValue();

				sliderForm.events.on(ItemEvent.change, (value: number[]) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				sliderForm.events.on(ItemEvent.beforeHide, (value: number[], init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				sliderForm.events.on(ItemEvent.beforeShow, (value: number[]) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				sliderForm.events.on(ItemEvent.afterHide, (value: number[], init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				sliderForm.events.on(ItemEvent.afterShow, (value: number[]) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				sliderForm.events.on(ItemEvent.focus, (value: number[]) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				sliderForm.events.on(ItemEvent.blur, (value: number[]) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				sliderForm.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "textarea": {
				const textarea = (this._attachments[name] = new Textarea(null, item));
				this._state[name] = textarea.getValue();

				textarea.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				textarea.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = textarea.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.paint();
				});
				textarea.events.on(ItemEvent.change, (value: string | number) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				textarea.events.on(ItemEvent.beforeHide, (value: string | number, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				textarea.events.on(ItemEvent.beforeShow, (value: string | number) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				textarea.events.on(ItemEvent.afterHide, (value: string | number, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				textarea.events.on(ItemEvent.afterShow, (value: string | number) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				textarea.events.on(ItemEvent.beforeValidate, (value: string | number) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				textarea.events.on(ItemEvent.afterValidate, (value: string | number, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				textarea.events.on(ItemEvent.focus, (value: string) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				textarea.events.on(ItemEvent.blur, (value: string) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				textarea.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "text": {
				if (width === "content") {
					classList += " dhx_form-element--fit-content";
				}
				const text = (this._attachments[name] = new Text(null, item));
				this._state[name] = text.getValue();

				text.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				text.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = text.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.paint();
				});
				text.events.on(ItemEvent.change, (value: string | number) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				text.events.on(ItemEvent.beforeHide, (value: string | number, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				text.events.on(ItemEvent.beforeShow, (value: string | number) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				text.events.on(ItemEvent.afterHide, (value: string | number, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				text.events.on(ItemEvent.afterShow, (value: string | number) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				text.events.on(ItemEvent.beforeValidate, (value: string | number) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				text.events.on(ItemEvent.afterValidate, (value: string | number, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				break;
			}
			case "timepicker": {
				const timePicker = (this._attachments[name] = new TimePicker(
					null,
					item as ITimePickerConfig
				));
				this._state[name] =
					(config.value && timePicker.getValue(config.valueFormat === "timeObject")) || "";

				timePicker.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				timePicker.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = timePicker.getValue(config.valueFormat === "timeObject");
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.paint();
				});
				timePicker.events.on(ItemEvent.change, (value: string | ITimeObject) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				timePicker.events.on(ItemEvent.beforeHide, (value: string | ITimeObject, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				timePicker.events.on(ItemEvent.beforeShow, (value: string | ITimeObject) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				timePicker.events.on(ItemEvent.afterHide, (value: string | ITimeObject, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				timePicker.events.on(ItemEvent.afterShow, (value: string | ITimeObject) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				timePicker.events.on(ItemEvent.beforeValidate, (value: string | ITimeObject) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				timePicker.events.on(
					ItemEvent.afterValidate,
					(value: string | ITimeObject, isValid: boolean) => {
						this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
					}
				);
				timePicker.events.on(ItemEvent.focus, (value: string | ITimeObject) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				timePicker.events.on(ItemEvent.blur, (value: string | ITimeObject) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				timePicker.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "colorpicker": {
				const colorPicker = (this._attachments[name] = new ColorPicker(
					null,
					item as IColorPickerConfig
				));
				this._state[name] = colorPicker.getValue();

				colorPicker.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				colorPicker.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = colorPicker.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.paint();
				});
				colorPicker.events.on(ItemEvent.change, (value: string) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				colorPicker.events.on(ItemEvent.beforeHide, (value: string, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				colorPicker.events.on(ItemEvent.beforeShow, (value: string) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				colorPicker.events.on(ItemEvent.afterHide, (value: string, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				colorPicker.events.on(ItemEvent.afterShow, (value: string) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				colorPicker.events.on(ItemEvent.beforeValidate, (value: string) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				colorPicker.events.on(ItemEvent.afterValidate, (value: string, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				colorPicker.events.on(ItemEvent.focus, (value: string) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				colorPicker.events.on(ItemEvent.blur, (value: string) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				colorPicker.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "spacer":
			default: {
				const spacer = (this._attachments[name] = new Spacer(null, item));

				spacer.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				spacer.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.paint();
				});
				spacer.events.on(ItemEvent.beforeHide, (value: string, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				spacer.events.on(ItemEvent.beforeShow, (value: string) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				spacer.events.on(ItemEvent.afterHide, (value: string, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				spacer.events.on(ItemEvent.afterShow, (value: string) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				break;
			}
		}

		const cell: ICellConfig = {
			id: name,
			width,
			height,
			padding,
			css: classList,
		};

		if ("gravity" in item) {
			cell.gravity = item.gravity;
		}

		return cell;
	}
	protected _changeProps(name, props) {
		const properties = ["width", "height", "css", "padding"];
		for (const key in props) {
			if (properties.includes(key)) {
				this.layout.getCell(name).config[key] = props[key];
			}
		}
	}
	private _addLayoutItems(items: IItemConfig[]): any[] {
		return items.map(item => {
			if (isBlock(item)) {
				const layoutConfig: ILayoutConfig = { width: "content", height: "content" };
				this._createLayoutConfig(item, layoutConfig);
				return layoutConfig;
			}
			return this._addLayoutItem(item);
		});
	}
	private _checkLayoutConfig(config: IFormConfig, layoutConfig: ILayoutConfig) {
		if (isDefined(config.css)) {
			layoutConfig.css = config.css;
		}
		if (isDefined(config.title)) {
			layoutConfig.header = config.title;
		}
		if (isDefined(config.padding)) {
			layoutConfig.padding = config.padding;
		}
		if (isDefined(config.gravity)) {
			layoutConfig.gravity = config.gravity;
		}
		if (isDefined(config.width)) {
			layoutConfig.width = config.width;
		}
		if (isDefined(config.height)) {
			layoutConfig.height = config.height;
		}
		if (isDefined(config.align)) {
			layoutConfig.align = config.align;
		}
		return layoutConfig;
	}
	protected _createLayoutConfig(config: IFormConfig, layoutConfig: ILayoutConfig) {
		layoutConfig = this._checkLayoutConfig(config, layoutConfig);
		if (isDefined(config.rows)) {
			layoutConfig.rows = this._addLayoutItems(config.rows);
		} else if (isDefined(config.cols)) {
			layoutConfig.cols = this._addLayoutItems(config.cols);
		}
	}
	protected _initUI(container: HTMLElement | string) {
		const attachments = (this._attachments = {});
		const layoutConfig: ILayoutConfig = {
			padding: "8px",
		};
		this.config.css += " dhx_form";

		this._createLayoutConfig(this.config, layoutConfig);

		const layout = (this.layout = new Layout(container, layoutConfig));

		for (const name in attachments) {
			layout.getCell(name).attach(attachments[name]);
		}
	}
	private _initHandlers() {
		this.events.on(FormEvents.afterShow, () => {
			this._formContainerShow();
		});
		this.events.on(FormEvents.afterHide, () => {
			if (!Object.values(this._attachments).some(item => !item.config.hidden)) {
				this._formContainerHide();
			}
		});
	}
	private _clear(): void {
		this._state = {};
		for (const key in this._attachments) {
			const name = this._attachments[key].config.name;
			if (typeof this._attachments[key].clear === "function") {
				this._attachments[key].clear();
				name
					? (this._state[name] = this._attachments[key].getValue())
					: (this._state[key] = this._attachments[key].getValue());
			}
		}
	}
	private _clearValidate(): void {
		for (const key in this._attachments) {
			if (typeof this._attachments[key].clearValidate === "function") {
				this._attachments[key].clearValidate();
			}
		}
	}
	private _formContainerShow(): void {
		this.config.hidden = false;

		if (!this.getRootView().node) return;

		const root = this.getRootView().node.el;
		root.style.display = "flex";
	}
	private _formContainerHide(): void {
		this.config.hidden = true;

		if (!this.getRootView().node) return;

		const root = this.getRootView().node.el;
		root.style.display = "none";
	}
	private _send() {
		for (const key in this._attachments) {
			if (typeof this._attachments[key].send === "function") {
				this._attachments[key].send();
			}
		}
	}
}
